<template>
  <StepLayout
    ref="stepRef"
    :title="steps[currentStep]?.title"
    :count="count"
    :showBack="!scanning"
    @back="$emit('back')"
  >
    <template #button="{ isMobile }">
      <ActionButton
        :variant="variant"
        :disabled="scanning && !isDirty"
        :hide="!variant || !canUpdate"
        :buildingId="building.id"
        :isMobile="isMobile"
        @goToStep="handleGoToStep"
      />
    </template>
    <Component
      ref="componentRef"
      :is="steps[currentStep]?.content"
      v-bind="steps[currentStep]?.props"
      @update:item="$emit('update:item', $event)"
      @back="handleStepBack"
    />
  </StepLayout>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { ActionButton, StepLayout } from "@/SharedComponents/Passport";
import {
  QrCodeList,
  QrCodeScan,
} from "@/SharedComponents/Passport/Steps/QrCodes";
import { useSteps, Steps } from "@/Composables/useSteps";
import { useQrCodeScanner } from "@/Composables/useQrCodeScanner";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["back", "update:item"]);

const stepRef = ref(null);
const componentRef = ref(null);

const props = defineProps({
  item: { type: Object, required: true },
  type: { type: String, required: true },
  building: { type: Object, required: true },
  canUpdate: { type: Boolean, required: false },
});

const {
  runningInWebView,
  getWebViewBridge,
  openQrCodeScanner,
  updateQrCode,
  _updateQrCode,
} = useQrCodeScanner(props.item, (item) => emit("update:item", { item }));

const { t } = useI18n();

const steps = computed(
  () =>
    new Steps({
      overview: {
        title: t("components.passport.qr_codes.label", 2),
        content: QrCodeList,
        props: {
          item: props.item,
          canUpdate: props.canUpdate,
        },
      },
      scan: {
        title: t("components.passport.qr_codes.label_add"),
        content: QrCodeScan,
        props: {
          type: props.type,
          updateFn: _updateQrCode,
        },
      },
    })
);

const {
  currentStep,
  handleGoToStep: _handleGoToStep,
  handleStepBack: _handleStepBack,
  reset,
} = useSteps(steps.value);

const count = computed(() => {
  switch (currentStep.value) {
    case "overview":
      return props.item.qr_codes.length;
    default:
      return undefined;
  }
});

const variant = computed(() => {
  switch (currentStep.value) {
    case "overview":
      return "add-qr-code";
    default:
      return undefined;
  }
});

const scanning = computed(() => currentStep.value === "scan");

function handleGoToStep(step) {
  if (step === "scan" && runningInWebView()) {
    openQrCodeScanner();
  } else {
    _handleGoToStep(step);
  }
}

function handleStepBack() {
  if (props.item.qr_codes.length > 0) {
    _handleStepBack();
  } else {
    emit("back");
  }
}

onMounted(() => {
  if (runningInWebView()) {
    getWebViewBridge().addEventListener("onQrCodeScanned", updateQrCode);
  }

  reset();
  if (props.item.qr_codes.length === 0) {
    handleGoToStep("scan");
  }
});

onUnmounted(() => {
  if (runningInWebView()) {
    getWebViewBridge().removeEventListener("onQrCodeScanned", updateQrCode);
  }
});
</script>
